import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";


function statusBadge(status) {
  const base = "px-2 py-1 rounded-full text-xs font-semibold";
  if (status === "approved") return `${base} bg-green-100 text-green-800`;
  if (status === "superseded") return `${base} bg-gray-200 text-gray-800`;
  if (status === "canceled") return `${base} bg-red-100 text-red-800`;
  if (status === "pending") return `${base} bg-yellow-100 text-yellow-800`;
  if (status === "draft") return `${base} bg-blue-100 text-blue-800`;
  return `${base} bg-slate-100 text-slate-800`;
}

function fmtMonth(year, month) {
  if (!year || !month) return "";
  return `${String(month).padStart(2, "0")}/${year}`;
}

function fmtDate(dt) {
  if (!dt) return "";
  try {
    const d = new Date(dt);
    return d.toLocaleString("cs-CZ");
  } catch {
    return "";
  }
}

export default function ShiftPlansHistoryPage() {
  const { user } = useAuth();

  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";
  const basePlannerPath = isAdmin ? "/admin/shifts" : "/shifts";

  const openInPlanner = (it) => {
    // ShiftsPage má review režim přes query params
    const qs = new URLSearchParams({
      mode: "review",
      pharmacy: String(it.pharmacyCode),
      year: String(it.year),
      month: String(it.month),
    }).toString();

    navigate(`${basePlannerPath}?mode=review&planId=${it._id}`);
  };

  const createNewVersion = async (it) => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `/api/shifts/${it.firma}/${it.pharmacyCode}/${it.year}/${it.month}/version`,
        { method: "POST", credentials: "include" }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || data?.message || "Nepodařilo se vytvořit novou verzi.");
      }

      // po vytvoření nové verze přejdi do plánovače na stejný měsíc/pobočku
      const qs = new URLSearchParams({
        mode: "review",
        pharmacy: String(data.pharmacyCode),
        year: String(data.year),
        month: String(data.month),
      }).toString();

      navigate(`${basePlannerPath}?mode=review&planId=${data._id}`);
    } catch (e) {
      setError(e?.message || "Chyba.");
    } finally {
      setLoading(false);
    }
  };

  
  const firma = user?.firma || "LEMON";

  const [pharmacyCode, setPharmacyCode] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState(""); // default (prázdné) = jen “hotové” dle backendu

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set("firma", firma);

    if (pharmacyCode.trim()) p.set("pharmacyCode", pharmacyCode.trim());
    if (year) p.set("year", String(year));
    if (month) p.set("month", String(month));
    if (status) p.set("status", status);

    return p.toString();
  }, [firma, pharmacyCode, year, month, status]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`/api/shifts/history?${queryString}`, {
          credentials: "include",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.message || "Chyba při načítání historie plánů.");
        }

        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Chyba.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Historie plánů směn</h1>
          <p className="text-gray-500 mt-1">
            Zobrazuje schválené / nahrazené (superseded) / zrušené plány. Inbox schvalování je zvlášť.
          </p>
        </div>
      </div>

      {/* Filtry */}
      <div className="mt-6 bg-white rounded-2xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Pobočka (pharmacyCode)</label>
            <input
              value={pharmacyCode}
              onChange={(e) => setPharmacyCode(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2"
              placeholder="např. 64"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Rok</label>
            <input
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2"
              placeholder="např. 2026"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Měsíc</label>
            <input
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2"
              placeholder="1–12"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2 bg-white"
            >
              <option value="">(default) hotové: approved/superseded/canceled</option>
              <option value="approved">approved</option>
              <option value="superseded">superseded</option>
              <option value="canceled">canceled</option>
              <option value="pending">pending</option>
              <option value="draft">draft</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                // jen “retrigger” přes změnu queryString neuděláme – ale toto je jednoduchý refresh
                // (stejně se refetch děje automaticky při změně filtrů)
                setYear((v) => v);
              }}
              className="w-full rounded-xl bg-blue-700 text-white font-semibold px-4 py-2 hover:bg-blue-800"
            >
              Obnovit
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 text-red-600 font-semibold">
            {error}
          </div>
        )}
      </div>

      {/* Tabulka */}
      <div className="mt-6 bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Nalezeno: <span className="font-bold text-gray-900">{items.length}</span>
          </div>
          {loading && <div className="text-sm text-gray-500">Načítám…</div>}
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3">Pobočka</th>
                <th className="text-left px-4 py-3">Měsíc</th>
                <th className="text-left px-4 py-3">Verze</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Schváleno</th>
                <th className="text-left px-4 py-3">Upraveno</th>
                <th className="text-right px-4 py-3">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{it.pharmacyCode}</td>
                  <td className="px-4 py-3">{fmtMonth(it.year, it.month)}</td>
                  <td className="px-4 py-3">{it.version ?? "-"}</td>
                  <td className="px-4 py-3">
                    <span className={statusBadge(it.status)}>{it.status}</span>
                  </td>
                  <td className="px-4 py-3">{fmtDate(it.approvedAt)}</td>
                  <td className="px-4 py-3">{fmtDate(it.updatedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => openInPlanner(it)}
                        className="px-3 py-1.5 rounded-xl border font-semibold hover:bg-white disabled:opacity-50"
                        disabled={loading}
                        title="Otevře plán v plánovači (review)"
                      >
                        Otevřít
                      </button>

                      <button
                        onClick={() => createNewVersion(it)}
                        className="px-3 py-1.5 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-50"
                        disabled={loading || it.status !== "approved"}
                        title={it.status !== "approved" ? "Novou verzi lze vytvořit jen ze schválené verze" : "Vytvoří novou verzi (draft) ze schválené"}
                      >
                        Nová verze
                      </button>
                    </div>
                  </td>
                  
                </tr>
              ))}

              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-gray-500" colSpan={7}>
                    Nic nenalezeno (zkus vyčistit filtry nebo změnit status).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
}
